"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.EscrowFetcher = void 0;
const web3_js_1 = require("@solana/web3.js");
const fetcher_1 = require("./fetcher");
const types_1 = require("../types");
const anchor_1 = require("@project-serum/anchor");
const parsing_1 = require("./parsing");
const utils_1 = require("./utils");
const bytes_1 = require("@project-serum/anchor/dist/cjs/utils/bytes");
class EscrowFetcher {
    constructor(connection, pda, cache) {
        this.fetcher = new fetcher_1.AccountFetcher(connection, cache);
        this.pda = pda;
        this.connection = connection;
    }
    getConfigData() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.fetcher.refreshAll();
            const configPDA = this.pda.getConfigPDA();
            return yield this.fetcher.getConfig(configPDA.key);
        });
    }
    getMarketData(tokenMint) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.fetcher.refreshAll();
            const configPDA = this.pda.getMarketPDA(tokenMint);
            return yield this.fetcher.getMarketState(configPDA.key);
        });
    }
    getConfigAddress() {
        return this.pda.getConfigPDA().key;
    }
    getMarketAddress(tokenMint) {
        return this.pda.getMarketPDA(tokenMint).key;
    }
    getTradeInfo(tradeId, refresh = false) {
        return __awaiter(this, void 0, void 0, function* () {
            const tradePDA = this.pda.getTradePDA(tradeId);
            // Get the TradeData
            const tradeData = yield this.fetcher.getTradeData(tradePDA.key, refresh);
            if (!tradeData) {
                return null;
            }
            const tradeDataInfo = Object.assign(Object.assign({}, tradeData), { pubkey: tradePDA.key });
            return tradeDataInfo;
        });
    }
    getTrades(startTradeId, endTradeId, refresh = false) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.fetcher.refreshAll();
            const configData = yield this.getConfigData();
            if (!configData) {
                throw new Error("Config data not found");
            }
            let currentTradeId = configData.currentTradeId.toNumber();
            if (endTradeId) {
                if (endTradeId < currentTradeId) {
                    currentTradeId = endTradeId;
                }
            }
            if (startTradeId >= currentTradeId) {
                throw new Error("Invalid startTradeId");
            }
            const tradePubkeys = [];
            for (let i = startTradeId; i < currentTradeId; i++) {
                tradePubkeys.push(this.pda.getTradePDA(new anchor_1.BN(i)).key);
            }
            return yield this.fetcher.getTradeDatas(tradePubkeys, refresh);
        });
    }
    getTradesByStatus(tradeStatus, side) {
        return __awaiter(this, void 0, void 0, function* () {
            let offset = (0, utils_1.getTradeStateOffSet)();
            let bytes = (0, utils_1.mapTradeStateToByte)(tradeStatus);
            if (side) {
                offset = (0, utils_1.getTradeStateOffSet)() - 1; // side is 1 byte before trade state
                let tradeSideBytes = bytes_1.bs58.decode((0, utils_1.mapSideToByte)(side));
                let tradeStateBytes = bytes_1.bs58.decode((0, utils_1.mapTradeStateToByte)(tradeStatus));
                bytes = bytes_1.bs58.encode(Buffer.concat([tradeSideBytes, tradeStateBytes])); // concatenate buffers
            }
            const allTrades = yield this.connection.getProgramAccounts(this.pda.programId, {
                filters: [
                    {
                        dataSize: types_1.TRADE_SIZE,
                    },
                    {
                        memcmp: {
                            offset,
                            bytes,
                        },
                    },
                ],
            });
            let parsedTrades = [];
            for (let trade of allTrades) {
                let value = parsing_1.ParsableTrade.parse(trade.account.data);
                if (value === null || value === void 0 ? void 0 : value.configAccount.equals(this.pda.getConfigPDA().key)) {
                    parsedTrades.push(Object.assign(Object.assign({}, value), { pubkey: new web3_js_1.PublicKey(trade.pubkey) }));
                }
            }
            // Filter out null values
            return parsedTrades.filter(Boolean);
        });
    }
    getTradesByBuyerAddress(address) {
        return __awaiter(this, void 0, void 0, function* () {
            const allTrades = yield this.connection.getProgramAccounts(this.pda.programId, {
                filters: [
                    {
                        dataSize: types_1.TRADE_SIZE,
                    },
                    {
                        memcmp: {
                            offset: (0, utils_1.getTradeBuyerAddressOffset)(),
                            bytes: address.toBase58(),
                        },
                    },
                ],
            });
            let parsedTrades = [];
            for (let trade of allTrades) {
                let value = parsing_1.ParsableTrade.parse(trade.account.data);
                if (value === null || value === void 0 ? void 0 : value.configAccount.equals(this.pda.getConfigPDA().key)) {
                    parsedTrades.push(Object.assign(Object.assign({}, value), { pubkey: new web3_js_1.PublicKey(trade.pubkey) }));
                }
            }
            // Filter out null values
            return parsedTrades.filter(Boolean);
        });
    }
    getTradesBySellerAddress(address) {
        return __awaiter(this, void 0, void 0, function* () {
            const allTrades = yield this.connection.getProgramAccounts(this.pda.programId, {
                filters: [
                    {
                        dataSize: types_1.TRADE_SIZE,
                    },
                    {
                        memcmp: {
                            offset: (0, utils_1.getTradeSellerAddressOffset)(),
                            bytes: address.toBase58(),
                        },
                    },
                ],
            });
            let parsedTrades = [];
            for (let trade of allTrades) {
                let value = parsing_1.ParsableTrade.parse(trade.account.data);
                if (value === null || value === void 0 ? void 0 : value.configAccount.equals(this.pda.getConfigPDA().key)) {
                    parsedTrades.push(Object.assign(Object.assign({}, value), { pubkey: new web3_js_1.PublicKey(trade.pubkey) }));
                }
            }
            // Filter out null values
            return parsedTrades.filter(Boolean);
        });
    }
    /**
     *
     * @param offerStatus Awaiting || Canceled || OutOfFund
     * @param side Buy || Sell
     * @returns
     */
    getOffersByStatus(offerStatus, side) {
        return __awaiter(this, void 0, void 0, function* () {
            let offset = (0, utils_1.getOfferStateOffset)();
            let bytes = (0, utils_1.mapOfferStateToByte)(offerStatus);
            if (side) {
                offset = offset - 1; // side is 1 byte before offer state
                let offerSideBytes = bytes_1.bs58.decode((0, utils_1.mapSideToByte)(side));
                let offerStateBytes = bytes_1.bs58.decode((0, utils_1.mapOfferStateToByte)(offerStatus));
                bytes = bytes_1.bs58.encode(Buffer.concat([offerSideBytes, offerStateBytes])); // concatenate buffers
            }
            const allOffers = yield this.connection.getProgramAccounts(this.pda.programId, {
                filters: [
                    {
                        dataSize: types_1.OFFER_SIZE,
                    },
                    {
                        memcmp: {
                            offset,
                            bytes,
                        },
                    },
                ],
            });
            let parsedOffers = [];
            for (let offer of allOffers) {
                let value = parsing_1.ParsableOffer.parse(offer.account.data);
                if (value === null || value === void 0 ? void 0 : value.configAccount.equals(this.pda.getConfigPDA().key)) {
                    parsedOffers.push(Object.assign(Object.assign({}, value), { pubkey: new web3_js_1.PublicKey(offer.pubkey) }));
                }
            }
            // Filter out null values
            return parsedOffers.filter(Boolean);
        });
    }
    getOffersByMakerAddress(address) {
        return __awaiter(this, void 0, void 0, function* () {
            const allOffers = yield this.connection.getProgramAccounts(this.pda.programId, {
                filters: [
                    {
                        dataSize: types_1.OFFER_SIZE,
                    },
                    {
                        memcmp: {
                            offset: (0, utils_1.getOfferMakerAddressOffset)(),
                            bytes: address.toBase58(),
                        },
                    },
                ],
            });
            let parsedOffers = [];
            for (let offer of allOffers) {
                let value = parsing_1.ParsableOffer.parse(offer.account.data);
                if (value === null || value === void 0 ? void 0 : value.configAccount.equals(this.pda.getConfigPDA().key)) {
                    parsedOffers.push(Object.assign(Object.assign({}, value), { pubkey: new web3_js_1.PublicKey(offer.pubkey) }));
                }
            }
            // Filter out null values
            return parsedOffers.filter(Boolean);
        });
    }
    getOffer(offerId) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.fetcher.refreshAll();
            const offerPubKey = this.pda.getOfferPDA(offerId).key;
            console.log("offerPubKey", offerPubKey.toBase58());
            const offerData = yield this.fetcher.getOfferData(offerPubKey);
            return offerData
                ? Object.assign(Object.assign({}, offerData), { pubkey: offerPubKey }) : null;
        });
    }
    getAllTradesOfAnOffer(offerId) {
        return __awaiter(this, void 0, void 0, function* () {
            const allTrades = yield this.connection.getProgramAccounts(this.pda.programId, {
                filters: [
                    {
                        dataSize: types_1.TRADE_SIZE,
                    },
                    {
                        memcmp: {
                            offset: (0, utils_1.getTradeOfferIdOffset)(),
                            bytes: bytes_1.bs58.encode(offerId.toArrayLike(Buffer, "le", 8)),
                        },
                    },
                ],
            });
            let parsedTrades = [];
            for (let trade of allTrades) {
                let value = parsing_1.ParsableTrade.parse(trade.account.data);
                if (value === null || value === void 0 ? void 0 : value.configAccount.equals(this.pda.getConfigPDA().key)) {
                    parsedTrades.push(Object.assign(Object.assign({}, value), { pubkey: new web3_js_1.PublicKey(trade.pubkey) }));
                }
            }
            // Filter out null values
            return parsedTrades.filter(Boolean);
        });
    }
    getAllTrades() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.fetcher.refreshAll();
            const configData = yield this.getConfigData();
            if (!configData) {
                throw new Error("Config data not found");
            }
            const currentTradeId = configData.currentTradeId.toNumber();
            const tradePubkeys = [];
            for (let i = 1; i < currentTradeId; i++) {
                tradePubkeys.push(this.pda.getTradePDA(new anchor_1.BN(i)).key);
            }
            return yield this.fetcher.getTradeDatas(tradePubkeys);
        });
    }
    getKYCCertData(tokenMint, userPubkey) {
        return __awaiter(this, void 0, void 0, function* () {
            const kycCertPDA = this.pda.getKycCertPDA(userPubkey, tokenMint);
            return yield this.fetcher.getKycCertData(kycCertPDA.key);
        });
    }
    isArbitrator(userPubkey, tokenMint) {
        return __awaiter(this, void 0, void 0, function* () {
            const arbitratorCertPDA = this.pda.getArbitratorCertPDA(userPubkey, tokenMint);
            const arbiratorData = yield this.fetcher.getArbitratorCertData(arbitratorCertPDA.key);
            return arbiratorData != null;
        });
    }
    refreshAll() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.fetcher.refreshAll();
        });
    }
}
exports.EscrowFetcher = EscrowFetcher;
